import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchQnaList } from '../../services/AdminQnaService';
import './AdminQnaRedesign.css';

const PAGE_SIZE = 10;

export default function AdminQna() {
  const [qnaList, setQnaList] = useState([]);
  const [status, setStatus] = useState('all');
  const [inputQuery, setInputQuery] = useState('');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const getQnaList = async () => {
      try {
        const token = localStorage.getItem('token');
        const data = await fetchQnaList(token);
        if (!data.success) throw new Error('문의사항 조회 실패');
        setQnaList(Array.isArray(data.qna_list) ? data.qna_list : []);
      } catch (err) {
        console.error('문의사항 불러오기 실패:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    getQnaList();
  }, []);

  const filtered = useMemo(() => {
    const keyword = query.toLowerCase();
    return qnaList.filter((item) => {
      const replied = item.is_replied === true ||
        item.is_replied === 1 || item.is_replied === '1';

      if (status === 'replied' && !replied) return false;
      if (status === 'pending' && replied) return false;

      return !keyword || [item.title, item.name].some((value) =>
        String(value ?? '').toLowerCase().includes(keyword)
      );
    });
  }, [qnaList, status, query]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const search = () => {
    setQuery(inputQuery.trim());
    setCurrentPage(1);
  };

  const reset = () => {
    setInputQuery('');
    setQuery('');
    setStatus('all');
    setCurrentPage(1);
  };

  const changeStatus = (value) => {
    setStatus(value);
    setCurrentPage(1);
  };

  return (
    <div className="qm-page">
      <div className="qm-heading">
        <div>
          <h2>문의사항 관리</h2>
          <p>사용자 문의 및 답변 처리 현황</p>
        </div>
        <span className="qm-total">전체 {qnaList.length}건</span>
      </div>

      <div className="qm-panel">
        <div className="qm-filter">
          <label htmlFor="qm-status">답변상태</label>
          <select
            id="qm-status"
            value={status}
            onChange={(e) => changeStatus(e.target.value)}
          >
            <option value="all">전체</option>
            <option value="pending">미답변</option>
            <option value="replied">답변완료</option>
          </select>
        </div>

        <div className="qm-search">
          <input
            type="text"
            placeholder="제목, 작성자 검색"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) search();
            }}
          />
          <button type="button" onClick={search}>검색</button>
          <button type="button" onClick={reset}>초기화</button>
        </div>

        <div className="qm-table-top">
          <strong>조회 결과 {filtered.length}건</strong>
          <span>문의사항을 선택하면 상세 내용을 확인할 수 있습니다.</span>
        </div>

        <div className="qm-table-wrap">
          <table className="qm-table">
            <colgroup>
              <col style={{ width: '8%' }} />
              <col style={{ width: '48%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>No</th>
                <th>제목</th>
                <th>작성자</th>
                <th>작성일</th>
                <th>답변상태</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? currentItems.map((item, index) => {
                const replied = item.is_replied === true ||
                  item.is_replied === 1 || item.is_replied === '1';
                return (
                  <tr
                    key={item.id}
                    tabIndex={0}
                    onClick={() => navigate(`/adminqna/${item.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/adminqna/${item.id}`);
                    }}
                  >
                    <td>{filtered.length - (currentPage - 1) * PAGE_SIZE - index}</td>
                    <td className="qm-title">{item.title || '-'}</td>
                    <td>{item.name || '알 수 없음'}</td>
                    <td>{item.create_date
                      ? new Date(item.create_date).toLocaleString('ko-KR')
                      : '-'}</td>
                    <td>
                      <span className={replied ? 'qm-replied' : 'qm-pending'}>
                        {replied ? '답변완료' : '미답변'}
                      </span>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="qm-empty">
                    {loading ? '문의사항을 불러오는 중입니다.'
                      : error ? '문의사항 조회에 실패했습니다.'
                      : '조회된 문의사항이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="qm-footer">
          <span>총 {filtered.length}건
            {totalPages > 0 && ` · ${currentPage} / ${totalPages} 페이지`}
          </span>
          {totalPages > 1 && (
            <div className="qm-pagination">
              <button disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}>«</button>
              <button disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => Math.floor((page - 1) / 5) ===
                  Math.floor((currentPage - 1) / 5))
                .map((page) => (
                  <button key={page}
                    className={currentPage === page ? 'active' : ''}
                    onClick={() => setCurrentPage(page)}>{page}</button>
                ))}
              <button disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}>›</button>
              <button disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}>»</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
